import { format, isSameDay } from 'date-fns';
import { cn } from '@/lib/cn';
import { HOUR_ROW_HEIGHT, WEEK_HOURS, minutesFromMidnight, timeLabel, type WeekDayCell } from '@/lib/weekCalendar';

export type CalendarVisualKind =
  | 'event'
  | 'meeting'
  | 'task'
  | 'note'
  | 'org-event'
  | 'holiday'
  | 'schedule'
  | 'pms-task'
  | 'pms-project';

export type CalendarVisualItem = {
  id: string;
  /** Identifier that matches `DayAgendaItem.id` so we can open item details. */
  agendaId?: string;
  kind: CalendarVisualKind;
  title: string;
  startsAt: string;
  endsAt: string;
  allDay: boolean;
  dateKey: string;
};

type Props = {
  days: WeekDayCell[];
  items: CalendarVisualItem[];
  selectedDate?: Date | null;
  attendanceByDay?: Record<string, 'present' | 'absent' | 'weekoff' | 'holiday'> | null;
  onSlotClick: (date: Date) => void;
  onDayHeaderClick?: (date: Date) => void;
  onItemClick?: (item: CalendarVisualItem, date: Date) => void;
};

const KIND_STYLE: Record<CalendarVisualKind, string> = {
  event: 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950 dark:text-blue-300 dark:border-blue-900',
  meeting: 'bg-fuchsia-50 text-fuchsia-700 border-fuchsia-200 dark:bg-fuchsia-950 dark:text-fuchsia-300 dark:border-fuchsia-900',
  task: 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950 dark:text-amber-300 dark:border-amber-900',
  note: 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950 dark:text-emerald-300 dark:border-emerald-900',
  'org-event': 'bg-indigo-50 text-indigo-700 border-indigo-200 dark:bg-indigo-950 dark:text-indigo-300 dark:border-indigo-900',
  holiday: 'bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-950 dark:text-rose-300 dark:border-rose-900',
  schedule: 'bg-slate-50 text-slate-700 border-slate-200 dark:bg-slate-900 dark:text-slate-300 dark:border-slate-800',
  'pms-task': 'bg-violet-500/10 text-violet-700 border-violet-200',
  'pms-project': 'bg-cyan-500/10 text-cyan-700 border-cyan-200',
};

const KIND_BLOCK: Record<CalendarVisualKind, string> = {
  event: 'border-l-blue-500 bg-blue-500/10 text-blue-700 dark:bg-blue-500/15 dark:text-blue-300',
  meeting: 'border-l-fuchsia-500 bg-fuchsia-500/10 text-fuchsia-700 dark:bg-fuchsia-500/15 dark:text-fuchsia-300',
  task: 'border-l-amber-500 bg-amber-500/10 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300',
  note: 'border-l-emerald-500 bg-emerald-500/10 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300',
  'org-event': 'border-l-indigo-500 bg-indigo-500/10 text-indigo-700 dark:bg-indigo-500/15 dark:text-indigo-300',
  holiday: 'border-l-rose-500 bg-rose-500/10 text-rose-700 dark:bg-rose-500/15 dark:text-rose-300',
  schedule: 'border-l-slate-400 bg-slate-500/10 text-slate-700 dark:bg-slate-500/15 dark:text-slate-300',
  'pms-task': 'border-l-violet-500 bg-violet-500/10 text-violet-700 dark:bg-violet-500/15 dark:text-violet-300',
  'pms-project': 'border-l-cyan-500 bg-cyan-500/10 text-cyan-700 dark:bg-cyan-500/15 dark:text-cyan-300',
};

type PositionedTimedItem = {
  item: CalendarVisualItem;
  startMinutes: number;
  endMinutes: number;
  lane: number;
  laneCount: number;
};

export function WeekTimeGrid({ days, items, selectedDate, attendanceByDay, onSlotClick, onDayHeaderClick, onItemClick }: Props) {
  const allDayItemsByDay = new Map<string, CalendarVisualItem[]>();
  const timedItemsByDay = new Map<string, CalendarVisualItem[]>();
  const allItemsByDay = new Map<string, CalendarVisualItem[]>();

  for (const item of items) {
    const target = item.allDay ? allDayItemsByDay : timedItemsByDay;
    const existing = target.get(item.dateKey) ?? [];
    existing.push(item);
    target.set(item.dateKey, existing);

    const combined = allItemsByDay.get(item.dateKey) ?? [];
    combined.push(item);
    allItemsByDay.set(item.dateKey, combined);
  }

  for (const [, list] of timedItemsByDay) {
    list.sort((a, b) => a.startsAt.localeCompare(b.startsAt));
  }
  for (const [, list] of allItemsByDay) {
    list.sort((a, b) => a.startsAt.localeCompare(b.startsAt));
  }

  const nowDate = new Date();
  const nowMinutes = nowDate.getHours() * 60 + nowDate.getMinutes();

  return (
    <section className="flex h-full flex-col overflow-hidden rounded-3xl border border-slate-100 bg-white dark:border-white/5 dark:bg-card">
      <div className="overflow-x-auto">
        <div
          className="sticky top-0 z-20 grid min-w-[760px] bg-white px-2 pt-3 dark:bg-card sm:min-w-0"
          style={{ gridTemplateColumns: `80px repeat(${days.length}, minmax(0, 1fr))` }}
        >
        <div className="flex items-center justify-center px-2 py-4 text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400">
          Time
        </div>
        {days.map((day) => {
          const isSelected = selectedDate && isSameDay(selectedDate, day.date);
          const dayKey = format(day.date, 'yyyy-MM-dd');
          const attendance =
            typeof attendanceByDay === 'object' && attendanceByDay ? attendanceByDay[dayKey] : undefined;
          return (
            <div
              key={day.date.toISOString()}
              className={cn(
                'px-1.5 pb-2'
              )}
            >
              <button
                type="button"
                onClick={() => onDayHeaderClick?.(day.date)}
                className={cn(
                  'group flex w-full flex-col items-center rounded-2xl px-3 py-2.5 text-center transition-all duration-200',
                  day.isToday
                    ? 'bg-gradient-to-br from-[#5B6CF6] to-[#8B5CF6] text-white'
                    : isSelected
                      ? 'bg-[#EEF2FF] text-[#5B47E8] dark:bg-[#1C1F3F] dark:text-[#8B7CFF]'
                      : 'text-[#0f172b] hover:bg-[#F3F4F8] dark:text-white dark:hover:bg-white/5'
                )}
              >
                <p className={cn(
                  'text-[10px] font-semibold uppercase tracking-[0.14em]',
                  day.isToday ? 'text-white/80' : 'text-slate-400'
                )}>
                  {day.dayLabel}
                </p>
                <span className="mt-1 text-xl font-bold leading-none">{day.dateLabel}</span>
                {attendance === 'present' ? (
                  <span className="mt-2 h-1.5 w-1.5 rounded-full bg-emerald-400" title="Present" />
                ) : attendance === 'absent' ? (
                  <span className="mt-2 h-1.5 w-1.5 rounded-full bg-rose-400" title="Absent" />
                ) : attendance === 'weekoff' ? (
                  <span className="mt-2 h-1.5 w-1.5 rounded-full bg-slate-400" title="Weekoff" />
                ) : attendance === 'holiday' ? (
                  <span className="mt-2 h-1.5 w-1.5 rounded-full bg-indigo-400" title="Holiday" />
                ) : (
                  <span className="mt-2 h-1.5 w-1.5 rounded-full bg-transparent" />
                )}
              </button>
            </div>
          );
        })}
        </div>
      </div>

      <div className="overflow-x-auto">
        <div
          className="mx-3 mb-2 grid min-w-[760px] rounded-2xl bg-[#F6F7FB] dark:bg-white/[0.03] sm:min-w-0"
          style={{ gridTemplateColumns: `80px repeat(${days.length}, minmax(0, 1fr))` }}
        >
        <div className="px-2 py-2.5 text-center text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400">
          All day
        </div>
        {days.map((day) => {
          const dayKey = format(day.date, 'yyyy-MM-dd');
          const allDayItems = allDayItemsByDay.get(dayKey) ?? [];
          const previewItems = allDayItems.slice(0, 3);
          const remainingCount = Math.max(allDayItems.length - previewItems.length, 0);

          return (
            <div key={`all-day-${dayKey}`} className="px-1.5 py-2">
              {previewItems.length ? (
                <div className="space-y-1">
                  {previewItems.map((item) => (
                    <button
                      key={`${item.id}-${item.dateKey}-${item.startsAt}`}
                      type="button"
                      onClick={() => onItemClick?.(item, day.date)}
                      className={cn(
                        'w-full truncate rounded-full border px-2.5 py-1 text-left text-[10px] font-semibold transition-opacity hover:opacity-90',
                        KIND_STYLE[item.kind]
                      )}
                      title={item.title}
                    >
                      {item.title}
                    </button>
                  ))}
                  {remainingCount > 0 ? (
                    <p className="px-1 text-[10px] font-medium text-slate-400">+{remainingCount} more</p>
                  ) : null}
                </div>
              ) : (
                <div className="h-6 rounded-full bg-white/70 dark:bg-white/5" />
              )}
            </div>
          );
        })}
        </div>
      </div>

      <div className="flex-1 overflow-auto custom-scrollbar relative">
        <div
          className="grid min-h-full min-w-[760px] sm:min-w-0"
          style={{ gridTemplateColumns: `80px repeat(${days.length}, minmax(0, 1fr))` }}
        >
          {/* Time Labels Column */}
          <div>
            {WEEK_HOURS.map((hour) => (
              <div
                key={`label-${hour}`}
                className="relative px-3 text-right text-[10px] font-medium text-slate-400"
                style={{ height: `${HOUR_ROW_HEIGHT}px` }}
              >
                <span className={cn('absolute right-3', hour === 0 ? 'top-1' : 'top-[-7px]')}>
                  {hour === 0 ? '12 AM' : hour < 12 ? `${hour} AM` : hour === 12 ? '12 PM' : `${hour - 12} PM`}
                </span>
              </div>
            ))}
          </div>

          {/* Day Columns */}
          {days.map((day) => {
            const dayKey = format(day.date, 'yyyy-MM-dd');
            const timed = timedItemsByDay.get(dayKey) ?? [];
            const positioned = positionTimedItems(timed);
            const isSelected = selectedDate && isSameDay(selectedDate, day.date);

            return (
              <div
                key={`timed-${dayKey}`}
                className={cn(
                  'group relative border-l border-slate-100/70 dark:border-white/[0.04]',
                  isSelected && 'bg-[#EEF2FF]/30 dark:bg-[#1C1F3F]/20'
                )}
              >
                {/* Hourly Slots */}
                {WEEK_HOURS.map((hour) => (
                  <button
                    key={`${dayKey}-${hour}`}
                    type="button"
                    className="block w-full border-b border-dashed border-slate-100 text-left transition-colors hover:bg-[#EEF2FF]/50 dark:border-white/5 dark:hover:bg-white/[0.03]"
                    style={{ height: `${HOUR_ROW_HEIGHT}px` }}
                    onClick={() => {
                      const slotDate = new Date(day.date);
                      slotDate.setHours(hour, 0, 0, 0);
                      onSlotClick(slotDate);
                    }}
                  />
                ))}

                {day.isToday ? (
                  <div
                    aria-hidden="true"
                    className="pointer-events-none absolute inset-x-0 z-10"
                    style={{ top: `${(nowMinutes / 60) * HOUR_ROW_HEIGHT}px` }}
                  >
                    <span className="absolute -left-1 -top-1 h-2 w-2 rounded-full bg-rose-500" />
                    <div className="h-px bg-rose-500/70" />
                  </div>
                ) : null}

                {/* Timed Items Overlay */}
                <div className="pointer-events-none absolute inset-0 py-[1px]">
                  {positioned.map((entry) => {
                    const { item, startMinutes, endMinutes, lane, laneCount } = entry;
                    const top = (startMinutes / 60) * HOUR_ROW_HEIGHT;
                    const height = ((endMinutes - startMinutes) / 60) * HOUR_ROW_HEIGHT;
                    const laneWidthPct = 100 / laneCount;
                    const leftPct = lane * laneWidthPct;
                    
                    return (
                      <article
                        key={`${item.id}-${item.dateKey}-${item.startsAt}`}
                        className={cn(
                          'group/item pointer-events-auto absolute cursor-pointer overflow-hidden rounded-xl border-l-[3px] px-2.5 py-1.5 text-[10px] transition-all duration-200 ease-out hover:z-50 hover:min-w-[160px] hover:scale-[1.02] hover:shadow-lg',
                          KIND_BLOCK[item.kind]
                        )}
                        style={{
                          top: `${top + 2}px`,
                          height: `${Math.max(height - 4, 32)}px`,
                          left: `${leftPct}%`,
                          width: `${laneWidthPct}%`,
                        }}
                        onClick={() => onItemClick?.(item, day.date)}
                      >
                        <div className="flex flex-col h-full gap-0.5 w-full">
                          <p className="truncate font-semibold leading-tight">{item.title}</p>
                          {height > 40 && (
                            <p className="opacity-70 font-medium truncate">
                              {timeLabel(item.startsAt)}
                            </p>
                          )}
                        </div>
                      </article>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function positionTimedItems(items: CalendarVisualItem[]): PositionedTimedItem[] {
  if (items.length === 0) return [];

  const positioned: PositionedTimedItem[] = items
    .map((item) => {
      const startMinutes = clampMinutes(minutesFromMidnight(item.startsAt));
      // Give all items at least 60 minutes of visual space so they aren't squished
      const endMinutes = Math.max(startMinutes + 60, clampMinutes(minutesFromMidnight(item.endsAt)));
      return { item, startMinutes, endMinutes, lane: 0, laneCount: 1 };
    })
    .sort((a, b) => a.startMinutes - b.startMinutes || a.endMinutes - b.endMinutes);

  const laneEnds: number[] = [];
  for (const entry of positioned) {
    let lane = laneEnds.findIndex((end) => end <= entry.startMinutes);
    if (lane === -1) {
      lane = laneEnds.length;
      laneEnds.push(entry.endMinutes);
    } else {
      laneEnds[lane] = entry.endMinutes;
    }
    entry.lane = lane;
  }

  let clusterStart = 0;
  let clusterEnd = positioned[0].endMinutes;
  for (let i = 1; i < positioned.length; i += 1) {
    const current = positioned[i];
    if (current.startMinutes < clusterEnd) {
      clusterEnd = Math.max(clusterEnd, current.endMinutes);
      continue;
    }
    applyClusterLaneCount(positioned, clusterStart, i);
    clusterStart = i;
    clusterEnd = current.endMinutes;
  }
  applyClusterLaneCount(positioned, clusterStart, positioned.length);

  return positioned;
}

function applyClusterLaneCount(items: PositionedTimedItem[], start: number, end: number) {
  let maxLane = 0;
  for (let i = start; i < end; i += 1) {
    maxLane = Math.max(maxLane, items[i].lane);
  }
  const laneCount = maxLane + 1;
  for (let i = start; i < end; i += 1) {
    items[i].laneCount = laneCount;
  }
}

function clampMinutes(minutes: number): number {
  if (minutes < 0) return 0;
  if (minutes > 24 * 60) return 24 * 60;
  return minutes;
}
